<template>
  <div :class="['UIAlert', `UIAlert--${status}`, `UIAlert--${variant}`]">
    <div class="UIAlert__content">
      <div class="UIAlert__icon-container">
        <UIIcon class="UIAlert__icon" :name="typeIconMap[status]" />
      </div>
      <div class="UIAlert__text">
        <h5 class="UIAlert__title">{{ title }}</h5>
        <div v-html="description" />
      </div>
      <UIButtonIcon icon="close" @click="() => $emit('close')" />
    </div>
    <div v-if="$slots.footer" class="UIAlert__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import UIButtonIcon from '@/views/components/ui/buttons/UIButtonIcon.vue'
import { NOTIFICATION_TYPE, NOTIFICATION_ICONS } from '@/types/notifications'

const typeIconMap = {
  [NOTIFICATION_TYPE.SUCCESS]: NOTIFICATION_ICONS.SUCCESS,
  [NOTIFICATION_TYPE.ERROR]: NOTIFICATION_ICONS.ERROR,
  [NOTIFICATION_TYPE.INFO]: NOTIFICATION_ICONS.INFO,
  [NOTIFICATION_TYPE.WARNING]: NOTIFICATION_ICONS.INFO,
  [NOTIFICATION_TYPE.NEUTRAL]: NOTIFICATION_ICONS.NEUTRAL,
}

defineEmits(['close'])

withDefaults(
  defineProps<{
    variant?: 'filled' | 'subtle'
    status?: NOTIFICATION_TYPE
    title?: string
    description?: string
  }>(),
  {
    variant: 'filled',
    status: NOTIFICATION_TYPE.INFO,
  },
)
</script>

<style lang="scss" scoped>
.UIAlert {
  @include bodyText();
  color: $color-ui-neutral-main;
  width: 100%;
  box-sizing: border-box;

  background: white;
  position: relative;
  pointer-events: auto;
  background: $color-white;
  // border-radius: 0.25rem;
  opacity: 1;
  width: 100%;
  max-width: 100%;

  &__content {
    padding: 1rem;
    display: flex;
    align-items: center;
    width: 100%;

    .UIAlert__icon-container {
      align-self: flex-start;

      .UIIcon-wrapper {
        --size: 1.25rem;
      }
    }

    .UIButtonIcon {
      --icon-size: 1.25rem;
      margin-left: 0.25rem;
    }
  }

  &__title {
    @include headingH3();
  }

  &__text {
    width: 100%;
    margin-left: 0.75rem;

    :deep(a) {
      color: $color-ui-accent-main;
      text-decoration: none;
    }
  }

  &__footer {
    width: 100%;
    border-top: 1px solid $color-ui-warning-10;
    padding: 0.5rem 1.5rem;
  }

  @mixin alertStatus($color, $bgColor: transparent, $iconColor: null) {
    border-color: $color;
    background-color: $bgColor;
    .UIAlert__icon {
      --color: #{if($iconColor, $iconColor, $color)};
    }
  }

  // Subtle variant (default styling)
  &--subtle {
    border-left: 0.25rem solid rgba(0, 0, 0, 0);
    &.UIAlert--success {
      @include alertStatus($color-ui-success-main, white);
    }
    &.UIAlert--error {
      @include alertStatus($color-ui-danger-main, white);
    }
    &.UIAlert--info {
      @include alertStatus($color-ui-info-main, white);
    }
    &.UIAlert--warning {
      @include alertStatus($color-ui-warning-main, white);
    }
  }

  // Filled variant
  &--filled {
    &.UIAlert--success {
      @include alertStatus(
        transparent,
        $color-ui-success-10,
        $color-ui-success-main
      );
    }
    &.UIAlert--error {
      @include alertStatus(
        transparent,
        $color-ui-danger-10,
        $color-ui-danger-main
      );
    }
    &.UIAlert--info {
      @include alertStatus(transparent, $color-ui-info-10, $color-ui-info-main);
    }
    &.UIAlert--warning {
      @include alertStatus(
        transparent,
        $color-ui-warning-10,
        $color-ui-warning-main
      );
    }
  }
}
</style>
